import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useUserProfile } from "./UserProfileContext";
import { doc, getFirestore, runTransaction } from "firebase/firestore";
import {
  buildOnboardingUpdate,
  normalizeOnboarding,
} from "../lib/onboarding";

const OnboardingContext = createContext(null);

export const OnboardingProvider = ({ children }) => {
  const { uid, profile, loading, error } = useUserProfile();
  const onboarding = useMemo(() => normalizeOnboarding(profile?.onboarding), [profile?.onboarding]);
  const profileLoaded = Boolean(uid) && !loading && !error;

  const persistOnboarding = useCallback(
    async (updates) => {
      if (!uid || !profileLoaded) throw new Error("Your profile is not ready. Please try again.");
      const db = getFirestore();
      const profileRef = doc(db, "users", uid);
      await runTransaction(db, async (transaction) => {
        const current = await transaction.get(profileRef);
        if (!current.exists()) throw new Error("Your profile is unavailable. Please try again.");
        transaction.update(profileRef, {
          onboarding: buildOnboardingUpdate(current.data().onboarding, updates),
        });
      });
    },
    [uid, profileLoaded],
  );

  const dismissWalkthrough = useCallback(async () => {
    await persistOnboarding({ status: "dismissed" });
  }, [persistOnboarding]);

  const completeWalkthrough = useCallback(async () => {
    await persistOnboarding({ status: "completed" });
  }, [persistOnboarding]);

  const markAnalysisHintSeen = useCallback(async () => {
    await persistOnboarding({ analysisHintSeen: true });
  }, [persistOnboarding]);

  const value = useMemo(
    () => ({
      onboarding,
      profileLoaded,
      dismissWalkthrough,
      completeWalkthrough,
      markAnalysisHintSeen,
    }),
    [
      completeWalkthrough,
      dismissWalkthrough,
      markAnalysisHintSeen,
      onboarding,
      profileLoaded,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);

  if (!context) {
    throw new Error("useOnboarding must be used inside OnboardingProvider");
  }

  return context;
};
